import React, { useMemo, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    HeaderFilter,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

const numberFormat = { type: "fixedPoint", precision: 2 };

/**
 * Reusable WM lot-master grid backed by wm/Lot/GetAllLots (server-side
 * DataSourceLoader), filtered by the selected item (`itemRowId` → R_Item).
 * Includes IMLot master + lot inventory-status columns.
 */
const CustomerLots = (props) => {
    const { itemRowId, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const allowedPageSizes = [10, 20, 100, 500, "all"];
    const stateKey = gridKey;
    const loadState = useCallback(() => GridLayout.sendStorageRequest(stateKey, "GET", accessToken, null, currentLayout), [stateKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(stateKey, "POST", accessToken, state, currentLayout), [stateKey, currentLayout]);

    const dataSource = useMemo(() => {
        if (!itemRowId) {
            return [];
        }
        const store = createStore({
            key: 'rowId',
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.Lot.GetAllLots}`,
            loadParams: { itemRowid: itemRowId },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        return new DataSource({ store });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemRowId]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            dataSource={dataSource}
            keyExpr="rowId"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            remoteOperations={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={stateKey} customSave={saveState} />

            {/* Default-visible columns */}
            <Column dataField="customerCode" caption="Customer ID" />
            <Column dataField="customerName" caption="Customer Name" />
            <Column dataField="itemCode" caption="Item Code" />
            <Column dataField="lotCode" caption="Lot Code" />
            <Column dataField="lotClassCode" caption="Lot Class" />
            <Column dataField="lotSubClassCode" caption="Lot Sub Class" />
            <Column dataField="lotDescription1" caption="Lot Description - 1" />
            <Column dataField="lotOnHand" caption="On Hand" dataType="number" format={numberFormat} />
            <Column dataField="lotAvailable" caption="Available" dataType="number" format={numberFormat} />
            <Column dataField="netAvailable" caption="Net Available" dataType="number" format={numberFormat} />

            {/* Column-chooser (hidden by default) */}
            <Column dataField="itemDescription1" caption="Item Description - 1" visible={false} />
            <Column dataField="itemDescription2" caption="Item Description - 2" visible={false} />
            <Column dataField="lotDescription2" caption="Lot Description - 2" visible={false} />
            <Column dataField="lotCountCycle" caption="Count Cycle" visible={false} />
            <Column dataField="lotInOrdered" caption="In Ordered" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotOutOrdered" caption="Out Ordered" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotInReceiving" caption="In Receiving" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotOutShipped" caption="Out Shipped" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotInBackOrder" caption="In Backorder" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotOutBackOrder" caption="Out Backorder" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotInUnavailable" caption="In Unavailable" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotOutUnavailable" caption="Out Unavailable" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotInFuture" caption="In Future" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotOutFuture" caption="Out Future" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="lotUDFCode1" caption="Code 1" visible={false} />
            <Column dataField="lotUDFCode2" caption="Code 2" visible={false} />
            <Column dataField="lotUDFCode3" caption="Code 3" visible={false} />
            <Column dataField="lotUDFCode4" caption="Code 4" visible={false} />
            <Column dataField="lotUDFCode5" caption="Code 5" visible={false} />
            <Column dataField="lotUDFCode6" caption="Code 6" visible={false} />
            <Column dataField="lotUDFCode7" caption="Code 7" visible={false} />
            <Column dataField="lotUDFCode8" caption="Code 8" visible={false} />
            <Column dataField="lotUDFCode9" caption="Code 9" visible={false} />
            <Column dataField="lotUDFCode10" caption="Code 10" visible={false} />
            <Column dataField="lotUDFRef1" caption="Reference 1" visible={false} />
            <Column dataField="lotUDFRef2" caption="Reference 2" visible={false} />
            <Column dataField="lotUDFRef3" caption="Reference 3" visible={false} />
            <Column dataField="lotUDFRef4" caption="Reference 4" visible={false} />
            <Column dataField="lotUDFRef5" caption="Reference 5" visible={false} />
            <Column dataField="lotUDFRef6" caption="Reference 6" visible={false} />
            <Column dataField="lotUDFRef7" caption="Reference 7" visible={false} />
            <Column dataField="lotUDFRef8" caption="Reference 8" visible={false} />
            <Column dataField="lotUDFRef9" caption="Reference 9" visible={false} />
            <Column dataField="lotUDFRef10" caption="Reference 10" visible={false} />
            <Column dataField="lotUDFDate1" caption="Date 1" dataType="date" visible={false} />
            <Column dataField="lotUDFDate2" caption="Date 2" dataType="date" visible={false} />
            <Column dataField="lotUDFDate3" caption="Date 3" dataType="date" visible={false} />
            <Column dataField="lotUDFDate4" caption="Date 4" dataType="date" visible={false} />
            <Column dataField="lotUDFDate5" caption="Date 5" dataType="date" visible={false} />
            <Column dataField="lotUDFDate6" caption="Date 6" dataType="date" visible={false} />
            <Column dataField="lotUDFDate7" caption="Date 7" dataType="date" visible={false} />
            <Column dataField="lotUDFDate8" caption="Date 8" dataType="date" visible={false} />
            <Column dataField="lotUDFDate9" caption="Date 9" dataType="date" visible={false} />
            <Column dataField="lotUDFDate10" caption="Date 10" dataType="date" visible={false} />
            <Column dataField="lotUDFQty1" caption="Quantity 1" dataType="number" visible={false} />
            <Column dataField="lotUDFQty2" caption="Quantity 2" dataType="number" visible={false} />
            <Column dataField="lotUDFQty3" caption="Quantity 3" dataType="number" visible={false} />
            <Column dataField="lotUDFQty4" caption="Quantity 4" dataType="number" visible={false} />
            <Column dataField="lotUDFQty5" caption="Quantity 5" dataType="number" visible={false} />
            <Column dataField="lotUDFQty6" caption="Quantity 6" dataType="number" visible={false} />
            <Column dataField="lotUDFQty7" caption="Quantity 7" dataType="number" visible={false} />
            <Column dataField="lotUDFQty8" caption="Quantity 8" dataType="number" visible={false} />
            <Column dataField="lotUDFQty9" caption="Quantity 9" dataType="number" visible={false} />
            <Column dataField="lotUDFQty10" caption="Quantity 10" dataType="number" visible={false} />
            <Column dataField="lotCompanyId" caption="Company ID" visible={false} />
            <Column dataField="lotModule" caption="Module" visible={false} />
            <Column dataField="lotRecordType" caption="Record Type" visible={false} />
            <Column dataField="lotCreationDateTime" caption="Creation Date" dataType="date" visible={false} />
            <Column dataField="lotCreationUserId" caption="Created By" visible={false} />
            <Column dataField="lotLastUpdateDateTime" caption="Last Updated" dataType="date" visible={false} />
            <Column dataField="lotLastUpdateUserId" caption="Last Updated By" visible={false} />
            <Toolbar>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CustomerLots);
